import type { GridApi } from "ag-grid-community";
import { atom } from "jotai";
import { unique } from "remeda";

import type { validColumnSettings } from "../columns";
import { strictRowStringToCode } from "@package/grid-utils";
import type { TPageFormattingOptions } from "../../../data";
import type { TRangeSelection } from "../useRangeSelectionChange";
import { getNodePeriod } from "../periodHelpers";
import { isGlobalStatus } from "../statuses/utils";

export const initialFormattingAndSettingsAtom = atom<{
  formatting: TPageFormattingOptions | null;
  settings: ReturnType<typeof validColumnSettings> | null;
}>({ formatting: null, settings: null });
initialFormattingAndSettingsAtom.debugLabel = "initialFormattingAndSettingsAtom";

export type TFormatTypes = "range" | "column" | "period";

export function modalTypeToHighlightType(type: string): "cell_highlights" | "column_highlights" | "period_highlights" {
  switch (type) {
    case "range":
      return "cell_highlights";
    case "column":
      return "column_highlights";
    case "period":
      return "period_highlights";
    default:
      return "cell_highlights";
  }
}

export function getCellIds({
  api,
  type,
  ranges,
}: {
  api: GridApi | null;
  type: TFormatTypes;
  ranges: TRangeSelection;
}) {
  const cellIds: string[] = [];
  const rowCodes: string[] = [];

  if (type === "range") {
    if (!ranges.length || !ranges[0].length) return { cellIds, rowCodes };

    if (api !== null) {
      for (const range of ranges) {
        for (const cell of range) {
          const colId = cell.columnId;

          const node = api.getRowNode(cell.rowId);
          const period = node !== undefined ? getNodePeriod(node) : undefined;

          if (colId && period) {
            const code = strictRowStringToCode(period);
            if (code && !rowCodes.includes(code)) {
              rowCodes.push(code);
            }

            const id = `${colId}-${period}`;
            if (!cellIds.includes(id)) {
              cellIds.push(id);
            }
          }
        }
      }
    }
  }

  if (type === "column") {
    for (const range of ranges) {
      for (const cell of range) {
        const colId = cell.columnId;
        if (colId && !cellIds.includes(colId)) {
          cellIds.push(colId);
        }
      }
    }
  }

  if (type === "period") {
    for (const range of ranges) {
      for (const cell of range) {
        const id = cell.rowId;
        if (!cellIds.includes(id)) {
          cellIds.push(id);
        }
      }
    }
  }

  return { cellIds: unique(cellIds), rowCodes: unique(rowCodes) };
}

const statuses = ["listen", "hybrid_broadcast", "private", "eod", "broadcast"];
export function getBackgroundForStatus(rawStatus: string) {
  const status = rawStatus.toLowerCase().replace("local", "private");

  if (isGlobalStatus(status)) {
    return "var(--global-color-bg)";
  }
  if (statuses.includes(status)) {
    return `var(--${status}-color-bg)`;
    // return `var(--${statusClasses[status as keyof typeof statusClasses]})`;
  }
  console.error(`Unknown status: ${status}`);
  return "inherit";
}

export function getForegroundForStatus(status: string) {
  if (isGlobalStatus(status)) {
    return "var(--global-color-fg)";
  }
  if (statuses.includes(status)) {
    return `var(--${status}-color-fg)`;
  }
  return "inherit";
}

export const getComputedColor = (color: string) => {
  if (color.startsWith("var(")) {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(color.replace("var(", "").replace(")", ""))
      .trim();
  }
  if (color.startsWith("--")) {
    return getComputedStyle(document.documentElement).getPropertyValue(color).trim();
  }
  return color;
};
